import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Feed from './Feed';
import { slugToName, categoryImages } from '../../utils/categoriesMap';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessToken } from '../../awsAuth';
import { trackOpenCategory } from '../../utils/trackActivity';
import './CategoryFeedPage.scss';

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  '';

const getAuthHeaders = async () => {
  const token = await getAccessToken();

  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const CategoryFeedPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { userId, isLoggedIn } = useAuth();

  const categoryName = slugToName[slug] || '';
  const icon = categoryImages[categoryName];

  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const trackedCategoryRef = useRef('');

  useEffect(() => {
    if (!categoryName) {
      navigate('/', { replace: true });
    }
  }, [categoryName, navigate]);

  useEffect(() => {
    const cleanCategory = String(categoryName || '').trim();

    if (!cleanCategory) return;
    if (trackedCategoryRef.current === cleanCategory) return;

    trackedCategoryRef.current = cleanCategory;

    trackOpenCategory({
      category: cleanCategory,
      slug,
    });
  }, [categoryName, slug]);

  useEffect(() => {
    let cancelled = false;

    async function loadStatus() {
      setErr('');

      if (!isLoggedIn || !userId || !categoryName) {
        setSubscribed(false);
        return;
      }

      try {
        const qs = new URLSearchParams({
          userId,
          category: categoryName,
        });

        const res = await fetch(
          `${API_BASE}/api/category-subs/status?${qs.toString()}`,
          {
            method: 'GET',
            headers: await getAuthHeaders(),
          }
        );

        if (!res.ok) {
          throw new Error(`status_${res.status}`);
        }

        const j = await res.json();

        if (!cancelled) {
          setSubscribed(!!j.subscribed);
        }
      } catch (e) {
        if (!cancelled) {
          setSubscribed(false);
          setErr('Не вдалося отримати статус підписки');
        }
      }
    }

    loadStatus();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, userId, categoryName]);

  const toggleSub = async () => {
    if (!isLoggedIn || !userId) {
      navigate('/login');
      return;
    }

    if (!categoryName) return;

    setBusy(true);
    setErr('');

    try {
      const url = subscribed
        ? `${API_BASE}/api/category-subs/unsubscribe`
        : `${API_BASE}/api/category-subs/subscribe`;

      const res = await fetch(url, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          userId,
          category: categoryName,
        }),
      });

      const j = await res.json().catch(() => ({}));

      if (!res.ok || j.error) {
        throw new Error(j.error || `status_${res.status}`);
      }

      setSubscribed((prev) => !prev);
    } catch (e) {
      setErr('Операція не вдалася');
    } finally {
      setBusy(false);
    }
  };

  if (!categoryName) {
    return null;
  }

  // Real category landing page — no route currently gives it a unique
  // title/description/canonical, so a live audit found the SPA served the
  // same generic "Cvanto" title (from public/index.html) for every single
  // category, which Search Console reports back as duplicate content with
  // no user-selected canonical. This is the smallest fix: give each
  // category its own real, non-duplicated identity without inventing any
  // content that isn't already on the page.
  const seoUrl = `https://cvanto.com/category/${slug}`;
  const seoTitle = `${categoryName} — оголошення на Cvanto`;
  const seoDesc = `Оголошення та дописи в категорії «${categoryName}» на Cvanto — маркетплейсі оголошень України.`;

  return (
    <div className="category-feed">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={seoUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cvanto" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={seoUrl} />
        {/* Brand fallback, not the category UI icon — sharing/crawling this
            page must not reinforce Google Images surfacing app icons instead
            of real listing photos (a confirmed live issue for this site). */}
        <meta property="og:image" content="https://cvanto.com/web-app-manifest-512x512.png" />
      </Helmet>

      <div className="category-feed__header">
        {icon && (
          <div
            className="category-feed__icon"
            style={{ backgroundImage: `url(${icon})` }}
          />
        )}

        <div className="category-feed__title">
          <h1>{categoryName}</h1>
          <p>Оголошення та дописи з цієї категорії</p>
        </div>

        <div className="category-feed__actions">
          <button
            className={`cat-sub-btn ${subscribed ? 'is-on' : ''}`}
            onClick={toggleSub}
            disabled={busy}
            title={
              subscribed
                ? 'Відписатись від категорії'
                : 'Підписатись на категорію'
            }
            type="button"
          >
            {busy ? 'Зачекайте…' : subscribed ? 'Відписатись' : 'Підписатись'}
          </button>

          <button
            className="category-feed__clear"
            onClick={() => navigate('/')}
            type="button"
          >
            Увесь фід
          </button>
        </div>
      </div>

      {err ? <div className="category-feed__error">{err}</div> : null}

      <Feed categoryName={categoryName} />
    </div>
  );
};

export default CategoryFeedPage;